import { fieldConfig } from "@autoform/zod";
import { z } from "zod";

import { MediaLenient } from "@/components/forms/schemas/media";
import { ENRICHMENT_STATUSES } from "@/lib/enrichment";
import {
  playCastNestedSchema,
  showGenreNestedSchema,
} from "@/pages/plays/schemas/play.nested.form";

const disabled = fieldConfig({ inputProps: { disabled: true } });

/**
 * Edit schema for a Play — the theater twin of `movieEditSchema`, bound to the full
 * `PlayDbDto` from `GET /api/plays/{id}` and mapped back onto `UpdatePlayCommand` by
 * `useTheaterRepository.updatePlay` (`genres` → `genreIds`, cast → `PlayCastRequestDto`).
 *
 * `translations` is deliberately absent so it round-trips untouched: partial updates only
 * send changed fields, and theater's translation flow is Phase 4.
 *
 * `slug` is nullish and must NEVER reach the API as `""` — see `updatePlay`.
 */
export const basePlayEditSchema = z.object({
  id: z.string().nullish().superRefine(disabled),
  title: z.string(),
  slug: z.coerce.string().nullish(),
  originalTitle: z.string(),
  // FluentValidation `MustBeIso6391` — a 2-letter code, not a locale.
  originalLanguage: z
    .string()
    .regex(/^[a-z]{2}$/, "Must be a 2-letter ISO 639-1 code, e.g. el")
    .nullish(),
  overview: z.string().superRefine(fieldConfig({ fieldType: "textarea" })),
  genres: z
    .array(showGenreNestedSchema)
    .default([])
    .superRefine(fieldConfig({ fieldType: "showGenres" })),
  cast: z
    .array(playCastNestedSchema)
    .default([])
    .superRefine(fieldConfig({ fieldType: "playCast" })),
  productionCompany: z.string().nullish(),
  durationInMinutes: z.coerce
    .number()
    .positive("Duration must be greater than 0")
    .nullish()
    .superRefine(fieldConfig({ inputProps: { min: 1 } })),
  intermission: z.coerce
    .number()
    .min(0, "Intermission cannot be negative")
    .nullish()
    .superRefine(fieldConfig({ inputProps: { min: 0 } })),
  status: z.enum(["Draft", "Complete", "Archived"]),
  tags: z
    .array(z.string())
    .nullish()
    .default([])
    .superRefine(fieldConfig({ fieldType: "tagList" })),
  enrichmentStatus: z
    .enum(ENRICHMENT_STATUSES)
    .nullish()
    .superRefine(fieldConfig({ fieldType: "enrichmentStatus" })),
  media: MediaLenient.superRefine(
    fieldConfig({ fieldType: "media", customData: { collection: "Plays" } }),
  ),
  hide: z.boolean().default(false),
  createdBy: z.string().nullish().superRefine(disabled),
  createdAt: z
    .string()
    .datetime({ offset: true })
    .nullish()
    .superRefine(
      fieldConfig({ fieldType: "datetime", inputProps: { disabled: true } }),
    ),
  updatedBy: z.string().nullish().superRefine(disabled),
  updatedAt: z
    .string()
    .datetime({ offset: true })
    .nullish()
    .superRefine(
      fieldConfig({ fieldType: "datetime", inputProps: { disabled: true } }),
    ),
});

export type PlayEditFormType = z.infer<typeof basePlayEditSchema> &
  Partial<{ premiereDate: string | null; ageGuidance: string | null }>;

/**
 * Which Phase-2 play fields this particular payload carries.
 *
 * `premiereDate` and `ageGuidance` ship on `PlayDbDto` as of admin-api 4.12.0, which
 * serializes both as `null` rather than omitting the key. Rather than a feature flag
 * someone has to remember to flip, the edit schema is still built per record from what the
 * wire actually returned: the read path is `.passthrough()`, so the keys survive, and
 * presence detection now exists as a tolerance for a document written by an older
 * admin-api, not a wait for the fields to ship. A key present but `null` counts — that is a
 * field that exists and is unset.
 */
export interface PlayOptionalFieldPresence {
  premiereDate: boolean;
  ageGuidance: boolean;
}

export function playFieldPresence(raw: unknown): PlayOptionalFieldPresence {
  const record = typeof raw === "object" && raw !== null ? raw : {};
  return {
    premiereDate: Object.hasOwn(record, "premiereDate"),
    ageGuidance: Object.hasOwn(record, "ageGuidance"),
  };
}

/**
 * Folds each cast entry's stored `role` (`PlayCastDbDto.Role`) into `characterName`
 * (`PlayCastRequestDto.CharacterName`) before the record ever reaches
 * `playCastNestedSchema` — see that schema's doc comment for why the fold cannot live on
 * the schema itself (`@autoform/zod` cannot walk a `z.preprocess`-wrapped array element).
 * An explicit `characterName` wins over a stale `role`; neither present yields `null`.
 * Called once, on the raw wire payload, before `playFieldPresence`/`parseRead` in
 * `PlaysEdit`.
 */
export function normalizePlayRecord(raw: unknown): unknown {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return raw;
  const record = raw as Record<string, unknown>;
  if (!Array.isArray(record.cast)) return record;
  return {
    ...record,
    cast: record.cast.map((entry) => {
      if (!entry || typeof entry !== "object" || Array.isArray(entry)) {
        return entry;
      }
      const cast = entry as Record<string, unknown>;
      return {
        ...cast,
        characterName: cast.characterName ?? cast.role ?? null,
      };
    }),
  };
}

export function buildPlayEditSchema(present: PlayOptionalFieldPresence) {
  let schema = basePlayEditSchema;
  if (present.premiereDate) {
    schema = schema.extend({
      premiereDate: z
        .string()
        .datetime({ offset: true })
        .nullish()
        .superRefine(fieldConfig({ fieldType: "datetime" })),
    });
  }
  if (present.ageGuidance) {
    schema = schema.extend({
      // `UpdatePlayCommandValidator`: `AgeGuidance` `MaximumLength(1000)` — 1000, not 200,
      // because Tina copies the theater page's advisory verbatim and a two-sentence
      // advisory is ordinary; the cap exists to stop an essay, not a sentence.
      ageGuidance: z
        .string()
        .max(1000, "Must be 1000 characters or fewer")
        .nullish()
        .superRefine(
          fieldConfig({
            label: "Age guidance",
            inputProps: { placeholder: "e.g. 12+ or Κατάλληλο για όλους" },
          }),
        ),
    });
  }
  return schema;
}
